import {
  Alert,
  AlertIcon,
  Badge,
  Icon,
  Box,
  Button,
  Card,
  CardBody,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  InputGroup,
  InputRightAddon,
  Link,
  Spinner,
  Stack,
  Text,
  VisuallyHidden,
  useToast,
} from "@chakra-ui/react";
import { FrameworkAgreementStoppedNotice } from "../view-single/FrameworkAgreementStoppedNotice";
import { useEffect } from "react";
import { FiEdit3, FiLock } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  Currency,
  PortalEventContextType,
  BidStatus,
  bffApi,
  useAcceptBidTermsMutation,
  useGetAuthenticatedPortalSourcingEventQuery,
  useGetBidQuery,
  useGetProductGroupsAsSupplierQuery,
  useGetPortalMyBidsQuery,
  useSubmitPortalPrivateBidMutation,
  useWithdrawPortalPrivateBidMutation,
  useUpdateBidMutation,
  type BidDto,
  type BidPatchDto,
  type MinimumRequirementDto,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import {
  coordinatorRegistry,
  useApiErrorEditorConfig,
  useCreateEditor,
  useEditor,
} from "../../../../common/editing/save-coordinator";
import { useApiError } from "../../../../common/errors/useApiError";
import { isInvitedCompetition } from "../isInvitedCompetition";
import { NumericInput } from "../../../../common/input/NumericInput";
import { Explanation } from "../../../../common/support/Explanation/Explanation";
import { TermsAcceptancePanel } from "./steps/TermsAcceptancePanel";
import { BidProductsSection } from "./BidProductsSection";
import { BidRejectionCard } from "./BidRejectionCard";
import { TipTap } from "../../../../common/input/TipTap/TipTap";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { useConfirmationModal } from "../../../../common/useConfirmationModal";
import { saveWithVersionConflictRetry } from "../../buyer/wizard/saveWithVersionConflictRetry";
import { CombinedDocuments } from "../../../../common/documents/CombinedDocuments";
import {
  type IncompleteFileUpload,
  isIncompleteFileUpload,
  UploadCompletedStatus,
} from "../../../../common/documents/UppyUploader/FileRow";
import {
  PendingUploadsProvider,
  usePendingUploadsTracker,
  useReportPendingUploads,
} from "../../../../common/documents/UppyUploader/pendingUploads";
import { useUppyUploader } from "../../../../common/documents/UppyUploader/useUppyUploader";
import { PortalLayout } from "../components/PortalLayout";
import { isRejectedInEvent } from "../ownPortalBid";
import { usePortalEventUrls } from "../../../../common/sourcing-events/portal/usePortalEventUrls";
import { PriceWeightBreakdown } from "../../../../common/award-criteria/PriceWeightBreakdown";
import { answerRowsOf, priceWeightRowsOf } from "../../../../common/award-criteria/scorableAwardRows";
import { AwardCriterionAnswerCard, isAwardCriterionAnswerComplete } from "./AwardCriterionAnswerCard";
import { MinimumRequirementAnswerCard } from "./MinimumRequirementAnswerCard";
import { useAwardCriterionAnswers } from "./useAwardCriterionAnswers";
import { useMinimumRequirementAnswers } from "./useMinimumRequirementAnswers";
import { isBidEditable } from "./publicBidEditability";
import { isBidLockedByDeadline, isSubmittedBidLockedByDeadline } from "./readonly/readonlyBidStatus";
import { usePriceCurrencyError } from "./usePriceCurrencyError";
import { displayDateWithTime } from "../../../../common/formatting/displayDateWithTime";
import { momentLocaleFor } from "../../../../common/formatting/i18nFormatters";
import { RoundBidDocumentsNotice } from "./RoundBidDocumentsNotice";
import { useRecordTitle } from "../../../../common/page-title/PageTitle";

export const EditPrivateBidPage = () => {
  const { t } = useTranslation();
  const { eventId, bidId } = useParams<{ eventId: string; bidId: string }>();
  const displayer = useApiError();
  const portalUrls = usePortalEventUrls();

  const bidQuery = useGetBidQuery({ bidId: bidId ?? "" }, { skip: !bidId });
  const eventQuery = useGetAuthenticatedPortalSourcingEventQuery({ eventId: eventId ?? "" }, { skip: !eventId });
  useRecordTitle(eventQuery.data?.title);

  if (bidQuery.error) return <PortalLayout>{displayer.show(bidQuery.error)}</PortalLayout>;
  if (eventQuery.error) return <PortalLayout>{displayer.show(eventQuery.error)}</PortalLayout>;

  if (!eventId || !bidId || !bidQuery.data || !eventQuery.data) {
    return (
      <PortalLayout>
        <Center py="24">
          <Spinner color="smPrimary" />
        </Center>
      </PortalLayout>
    );
  }

  if (!isInvitedCompetition(eventQuery.data.contextType)) {
    return (
      <PortalLayout>
        <Center
          py="16"
          px="6"
          mt="8"
          bg="smBackground"
          rounded="xl"
          border="1px solid"
          borderColor="smBorder"
          flexDirection="column"
          gap="3"
          color="smMuted"
        >
          <Icon as={FiLock} boxSize="28px" />
          <Text fontWeight="medium" color="smText">
            {t("This bid is not for a procurement you were invited to.")}
          </Text>
          <Text fontSize="sm" maxW="420px" textAlign="center">
            {t("Open competitions are bid for from the competition itself.")}
          </Text>
          <Button as={RouterLink} to={portalUrls.view.go(eventId)} colorScheme="teal" size="sm">
            {t("Go to the competition")}
          </Button>
        </Center>
      </PortalLayout>
    );
  }

  return (
    <EditPrivateBidForm eventId={eventId} bidId={bidId} bid={bidQuery.data} event={eventQuery.data} />
  );
};

interface FormProps {
  eventId: string;
  bidId: string;
  bid: BidDto;
  event: PortalSourcingEventDetailDto;
}

export const EditPrivateBidForm = ({ eventId, bidId, bid, event }: FormProps) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const navigate = useNavigate();
  const confirmationModal = useConfirmationModal();
  const { trigger } = useApiError();
  const portalUrls = usePortalEventUrls();
  const editorErrorConfig = useApiErrorEditorConfig();
  const [updateBid] = useUpdateBidMutation();
  const [submit, { isLoading: isSubmitting }] = useSubmitPortalPrivateBidMutation();
  const [withdraw, { isLoading: isWithdrawing }] = useWithdrawPortalPrivateBidMutation();
  const [acceptTerms, { isLoading: isAccepting }] = useAcceptBidTermsMutation();
  const { hasPendingUploads, report: reportPendingUploads } = usePendingUploadsTracker();
  const { data: productData, isSuccess: productsSettled } = useGetProductGroupsAsSupplierQuery({
    sourcingEventId: eventId,
  });
  const hasProducts = (productData?.productGroups ?? []).length > 0;
  const { data: myBids } = useGetPortalMyBidsQuery({});
  const rejectedInEvent = isRejectedInEvent(myBids?.bids, eventId, bidId);
  const agreementStopped = event.frameworkAgreementNoLongerActive === true;
  const readOnly = !isBidEditable(bid) || rejectedInEvent || agreementStopped;
  const isSubmitted = bid.status === BidStatus.Submitted;
  const isRejected = bid.status === BidStatus.Rejected;
  const lockedDraft = isBidLockedByDeadline(bid);
  const lockedSubmitted = isSubmittedBidLockedByDeadline(bid);
  const deadlineTime = bid.submissionDeadline
    ? displayDateWithTime(bid.submissionDeadline, null, momentLocaleFor(i18n.language))
    : null;

  const config = {
    initialState: bid,
    save: async (fields: Partial<BidPatchDto>) =>
      await saveWithVersionConflictRetry({
        fields,
        save: async (retryFields) =>
          await updateBid({ bidId, bidPatchDto: retryFields as BidPatchDto }).unwrap(),
        reloadLatest: async () => {
          await dispatch(bffApi.endpoints.getBid.initiate({ bidId }, { forceRefetch: true })).unwrap();
        },
      }),
    ...editorErrorConfig,
  };

  useCreateEditor<BidDto, BidPatchDto>(bidId, config);
  const minimumRequirementAnswers = useMinimumRequirementAnswers(bidId);
  const minimumRequirements = event.minimumRequirements ?? [];
  const awardCriterionAnswers = useAwardCriterionAnswers(bidId, event);

  const handleWithdraw = () => {
    confirmationModal.show({
      title: t("Edit bid?"),
      description: t(
        "Your submitted bid is withdrawn while you edit, and no longer counts as delivered until you submit it again.",
      ),
      confirmButtonText: t("Edit bid") ?? "Edit bid",
      onConfirmed: async () => {
        try {
          await withdraw({ eventId, bidId }).unwrap();
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  const handleAcceptTerms = async () => {
    try {
      await acceptTerms({ bidId }).unwrap();
    } catch (e) {
      trigger(e);
    }
  };

  const handleSubmit = () => {
    if (minimumRequirementAnswers.answeredCount(minimumRequirements) < minimumRequirements.length) {
      toast({
        title:
          minimumRequirementAnswers.commentRequiredCount(minimumRequirements) > 0
            ? t("Explain your answers to the minimum requirements before you submit")
            : t("Answer the buyer's minimum requirements before you submit"),
        status: "warning",
      });
      return;
    }
    const notMetCount = minimumRequirementAnswers.notMetCount(minimumRequirements);
    const unansweredCriteria = awardCriterionAnswers.rows.filter(
      (row) => !isAwardCriterionAnswerComplete(row, awardCriterionAnswers.responseFor(row), awardCriterionAnswers.price),
    );
    const pricedProducts = (bid.products ?? []).flatMap((group) => group.productBids ?? []);
    const offeredProducts = (productData?.productGroups ?? []).flatMap((group) => group.products ?? []);
    const unpricedProducts = offeredProducts.filter(
      (product) => pricedProducts.find((row) => row.productId === product.id)?.unitPrice?.value == null,
    ).length;
    const warnings = [
      unpricedProducts > 0
        ? t("{{count}} products have no unit price. The buyer scores the sum of the prices you enter.", {
            count: unpricedProducts,
          })
        : null,
      notMetCount > 0
        ? t("You answered No to {{count}} minimum requirements. The buyer may reject the bid.", { count: notMetCount })
        : null,
      unansweredCriteria.length > 0
        ? t("You have not answered {{count}} award criteria: {{names}}. The buyer scores the bid as delivered.", {
            count: unansweredCriteria.length,
            names: unansweredCriteria.map((row) => row.name).join(", "),
          })
        : null,
    ].filter((warning): warning is string => !!warning);
    confirmationModal.show({
      title: t("Submit bid?"),
      description: [
        ...warnings,
        t("Your bid will be delivered to the buyer. You can still revise it before the deadline."),
      ].join(" "),
      confirmButtonText: (warnings.length > 0 ? t("Submit anyway") : t("Submit bid")) ?? "Submit bid",
      onConfirmed: async () => {
        try {
          await coordinatorRegistry.flush(bidId);
          if (coordinatorRegistry.get(bidId)?.getEntityStatus() === "error") return;
          await submit({ eventId, bidId }).unwrap();
          toast({ title: t("Bid submitted!"), status: "success" });
          navigate(portalUrls.view.go(eventId));
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  return (
    <PendingUploadsProvider value={reportPendingUploads}>
      <PortalLayout>
        {confirmationModal.modal}
        {/* Clearance for the Intercom launcher, fixed over the viewport's bottom-right corner. */}
        <Stack spacing="6" pb={{ base: "16", md: "20" }}>
          <Box>
            <Link as={RouterLink} to={portalUrls.view.go(eventId)} color="smPrimary" fontSize="sm" fontWeight="semibold">
              ← {t("Back to the procurement")}
            </Link>
            <Flex justify="space-between" align="flex-start" flexWrap="wrap" rowGap="3" mt="2">
              <Box>
                <Heading as="h1" size="lg">
                  {isSubmitted ? t("Your submitted bid") : lockedDraft || isRejected || rejectedInEvent ? t("Your bid") : t("Prepare your bid")}
                  {event.title && (
                    <>
                      {" "}<VisuallyHidden>– </VisuallyHidden>
                      <Text
                        as="span"
                        display="block"
                        color="smMuted"
                        fontSize="sm"
                        fontFamily="body"
                        fontWeight="normal"
                        lineHeight="base"
                      >
                        {event.title}
                      </Text>
                    </>
                  )}
                </Heading>
                {isSubmitted && (
                  <Badge colorScheme="green" mt="2">
                    {t("Bid submitted")}
                  </Badge>
                )}
                {lockedDraft && deadlineTime && (
                  <Alert status="warning" borderRadius="md" fontSize="sm" mt="3">
                    <AlertIcon />
                    {t("The deadline passed {{time}}. The offer can no longer be changed or delivered.", {
                      time: deadlineTime,
                    })}
                  </Alert>
                )}
                {lockedSubmitted && deadlineTime && (
                  <Alert status="info" borderRadius="md" fontSize="sm" mt="3">
                    <AlertIcon />
                    {t("The deadline passed {{time}}. Your bid is delivered and can no longer be changed.", {
                      time: deadlineTime,
                    })}
                  </Alert>
                )}
              </Box>
              {isSubmitted && !lockedSubmitted && !rejectedInEvent && !agreementStopped && (
                <Button colorScheme="teal" leftIcon={<Icon as={FiEdit3} />} onClick={handleWithdraw} isLoading={isWithdrawing}>
                  {t("Edit bid")}
                </Button>
              )}
            </Flex>
          </Box>

          <FrameworkAgreementStoppedNotice event={event} />

          {rejectedInEvent && (
            <Alert status="warning" borderRadius="md" fontSize="sm" data-testid="rejected-in-event">
              <AlertIcon />
              {isSubmitted
                ? t("Your bid in this procurement has been rejected, so this bid is no longer part of the competition.")
                : t("Your bid in this procurement has been rejected, so this bid can no longer be delivered.")}
            </Alert>
          )}

          {isRejected && (
            <BidRejectionCard
              rejection={bid.rejection}
              buyerRunsPublicProcurements={event.buyerRunsPublicProcurements ?? false}
              outsideProcurementAct={event.outsideProcurementAct ?? false}
            />
          )}

          {/* Only when the award-criteria section is not rendering a price row of its own: it surfaces the
              buyer's PRICE criterion, or prepends a synthetic one, and both write bid.price. Two live
              fields for the same value would race on their own debounce timers. A product event asks
              for no free-standing total at all — the product prices are the price. */}
          {event.collectSupplierPrice !== false &&
            (event.awardCriteria?.criteria ?? []).length === 0 &&
            productsSettled &&
            !hasProducts && <PriceSection bidId={bidId} event={event} readOnly={readOnly} />}
          <MinimumRequirementsSection bidId={bidId} requirements={event.minimumRequirements ?? []} />
          <AwardCriteriaSection bidId={bidId} event={event} />
          <BidProductsSection
            bidId={bidId}
            eventId={eventId}
            readOnly={readOnly}
            currency={event.tenderCurrency ?? Currency.Nok}
          />
          <DocumentsSection bidId={bidId} readOnly={readOnly} />
          <CoverLetterSection bidId={bidId} readOnly={readOnly} />

          {bid.termsToAccept && (!readOnly || bid.termsAccepted) && (
            <TermsAcceptancePanel
              termsHtml={bid.termsToAccept}
              accepted={bid.termsAccepted ?? false}
              onAccept={handleAcceptTerms}
              isAccepting={isAccepting}
            />
          )}

          {!readOnly && (
            <Flex justify="flex-end" align="center" gap="3">
              {hasPendingUploads && (
                <Text fontSize="sm" color="smMuted">
                  {t("The bid can be delivered once the documents have finished uploading.")}
                </Text>
              )}
              <Explanation
                text={t("Accept the buyer's terms and conditions first")}
                enabled={!!bid.termsToAccept && !bid.termsAccepted}
                shouldWrapChildren
              >
                <Button
                  colorScheme="teal"
                  onClick={handleSubmit}
                  isLoading={isSubmitting}
                  isDisabled={(!!bid.termsToAccept && !bid.termsAccepted) || hasPendingUploads}
                >
                  {t("Submit bid")}
                </Button>
              </Explanation>
            </Flex>
          )}

          {isSubmitted && (
            <Card variant="outline" rounded="xl" borderColor="smSuccess" bg="smBackground">
              <CardBody>
                <Stack spacing="1">
                  <Text fontWeight="semibold" color="smSuccess">
                    {t("Bid submitted!")}
                  </Text>
                  <Text fontSize="sm" color="smMuted">
                    {t("Your bid has been delivered to the buyer. You will be notified of the outcome.")}
                  </Text>
                  <Text fontSize="sm" color="smMuted">
                    {t("A receipt has been sent by email.")}
                  </Text>
                </Stack>
              </CardBody>
            </Card>
          )}
        </Stack>
      </PortalLayout>
    </PendingUploadsProvider>
  );
};

interface SectionProps {
  bidId: string;
  readOnly: boolean;
}

const PriceSection = ({
  bidId,
  event,
  readOnly,
}: SectionProps & { event: { tenderCurrency?: Currency } }) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus, fieldError } = useEditor<BidDto, BidPatchDto>(bidId);
  const currency = event.tenderCurrency ?? Currency.Nok;
  const { shownCurrency, priceError } = usePriceCurrencyError(state.price, currency, fieldError("price"));
  const value = state.price?.value ?? null;
  const isSaving = fieldStatus("price") === "saving" || fieldStatus("price") === "pending";

  return (
    <Card variant="outline" rounded="xl">
      <CardBody>
        <Flex align="center" gap="2" mb="4">
          <Heading size="sm">{t("Price")}</Heading>
          {isSaving && <Spinner size="sm" color="smMuted" />}
        </Flex>
        <FormControl maxW="sm" isInvalid={priceError != null}>
          <FormLabel fontSize="sm">{t("Total price")}</FormLabel>
          <InputGroup>
            <NumericInput
              numericValue={value}
              onNumericChange={(next) => {
                if (!readOnly) updateField({ price: { value: next, currency } }, { debounceMs: 400 });
              }}
              isDisabled={readOnly}
              placeholder={t("Enter your total price") ?? ""}
            />
            <InputRightAddon>{shownCurrency}</InputRightAddon>
          </InputGroup>
          <FormErrorMessage>{priceError}</FormErrorMessage>
        </FormControl>
      </CardBody>
    </Card>
  );
};

const DocumentsSection = ({ bidId, readOnly }: SectionProps) => {
  const { t } = useTranslation();
  const { data: bid, refetch } = useGetBidQuery({ bidId });

  const { uppy, allFiles, documents, setDocuments, removeUpload } = useUppyUploader({
    entityType: "BidV2",
    entityId: bidId,
    reloadEntity: refetch,
  });
  useReportPendingUploads(allFiles.some(isIncompleteFileUpload));

  useEffect(() => {
    setDocuments(bid?.documents ?? []);
  }, [bid?.documents, setDocuments]);

  return (
    <Card variant="outline" rounded="xl">
      <CardBody>
        <Heading size="sm" mb="4">
          {t("Offer documents")}
        </Heading>
        <RoundBidDocumentsNotice bid={bid} mb="4" />
        <Box borderWidth="1px" borderColor="smBorder" rounded="lg" p="3">
          <CombinedDocuments
            uploadedDocuments={documents}
            setUploadedDocuments={setDocuments}
            removeUploadedDocument={(doc) =>
              removeUpload({
                status: UploadCompletedStatus.UploadCompleted,
                documentId: doc.id,
                uploadId: doc.fromUploadId,
                ...doc,
              })
            }
            isTagsColumnVisible={false}
            isCategoryColumnVisible={false}
            canDeleteDocuments={!readOnly}
            canEditDocumentNames={!readOnly}
            documentUploadConfig={
              readOnly
                ? undefined
                : {
                    uppy,
                    dropTargetId: `private-bid-documents-${bidId}`,
                    dropTargetHelperText: t("Drag and drop files here, or click to browse") ?? "",
                    uploads: allFiles.filter(isIncompleteFileUpload) as IncompleteFileUpload[],
                    removeUpload,
                  }
            }
          />
        </Box>
      </CardBody>
    </Card>
  );
};

const limitedToolbar = {
  underline: false,
  headings: false,
  colors: false,
  link: false,
  table: false,
  image: false,
  template: false,
};

// A private competition can weight award criteria too (ADR-035), and this page is its only delivery
// surface, so the same cards appear here rather than only in the public wizard step.
const AwardCriteriaSection = ({ bidId, event }: { bidId: string; event: PortalSourcingEventDetailDto }) => {
  const { t } = useTranslation();
  const answers = useAwardCriterionAnswers(bidId, event);
  if ((event.awardCriteria?.criteria ?? []).length === 0) return null;

  return (
    <Card variant="outline" rounded="xl">
      <CardBody>
        <Flex align="center" justify="space-between" gap="3" mb="4" flexWrap="wrap">
          <Flex align="center" gap="2">
            <Heading size="sm">{t("Award criteria")}</Heading>
            <Badge bg="smPrimarySofter" color="smPrimary" px="2" borderRadius="md" textTransform="none">
              {t("Buyer criteria")}
            </Badge>
            {answers.isSaving && <Spinner size="sm" color="smMuted" />}
          </Flex>
          <Text
            fontSize="sm"
            fontWeight="semibold"
            color={answers.answeredCount === answers.answerRows.length ? "smSuccess" : "smMuted"}
          >
            {t("{{answered}} of {{total}} answered", {
              answered: answers.answeredCount,
              total: answers.answerRows.length,
            })}
          </Text>
        </Flex>
        <Stack spacing="5">
          {answers.groups.map((group) => (
            <Stack key={group.key} spacing="3">
              {group.hasOwnHeading && (
                <Flex align="baseline" gap="3" flexWrap="wrap">
                  <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wide" color="smText">
                    {group.label}
                  </Text>
                  {group.weightPercent != null && (
                    <Text fontSize="sm" color="smMuted">
                      {t("Weight in evaluation")}: <b>{group.weightPercent} %</b>
                    </Text>
                  )}
                </Flex>
              )}
              {answerRowsOf(group).map((row) => (
                <AwardCriterionAnswerCard
                  key={row.id}
                  bidId={bidId}
                  row={row}
                  response={answers.responseFor(row)}
                  price={answers.price}
                  priceComesFromProducts={answers.priceComesFromProducts}
                  priceEditingLocked={answers.priceEditingLocked}
                  currency={answers.currency}
                  priceError={answers.priceError}
                  isReadOnly={answers.readOnly}
                  onChange={(patch) => answers.upsert(row, patch)}
                  onPriceChange={answers.setPrice}
                />
              ))}
              <PriceWeightBreakdown rows={priceWeightRowsOf(group)} />
            </Stack>
          ))}
        </Stack>
      </CardBody>
    </Card>
  );
};

// t('Minimum requirements') t('Buyer criteria') t('Answer the buyer's minimum requirements.')
const MinimumRequirementsSection = ({
  bidId,
  requirements,
}: {
  bidId: string;
  requirements: MinimumRequirementDto[];
}) => {
  const { t } = useTranslation();
  const answers = useMinimumRequirementAnswers(bidId);
  if (requirements.length === 0) return null;
  const answeredCount = answers.answeredCount(requirements);
  const notMetCount = answers.notMetCount(requirements);

  return (
    <Card variant="outline" rounded="xl">
      <CardBody>
        <Flex align="center" justify="space-between" gap="3" mb="4" flexWrap="wrap">
          <Flex align="center" gap="2">
            <Heading size="sm">{t("Minimum requirements")}</Heading>
            <Badge bg="smPrimarySofter" color="smPrimary" px="2" borderRadius="md" textTransform="none">
              {t("Buyer criteria")}
            </Badge>
            {answers.isSaving && <Spinner size="sm" color="smMuted" />}
          </Flex>
          <Text
            fontSize="sm"
            fontWeight="semibold"
            color={notMetCount > 0 ? "smWarning" : answeredCount === requirements.length ? "smSuccess" : "smMuted"}
          >
            {t("{{answered}} of {{total}} answered", { answered: answeredCount, total: requirements.length })}
            {notMetCount > 0 ? ` · ${t("{{count}} not met", { count: notMetCount })}` : ""}
          </Text>
        </Flex>
        <Text fontSize="sm" color="smMuted" mb="4">
          {t("Answer the buyer's minimum requirements.")}
        </Text>
        <Stack spacing="3">
          {requirements.map((requirement) => (
            <MinimumRequirementAnswerCard
              key={requirement.id ?? ""}
              bidId={bidId}
              requirement={requirement}
              response={answers.responseFor(requirement)}
              isReadOnly={answers.readOnly}
              onAnswerChange={(answer) =>
                answers.upsert(requirement, answer ? { answer: true, comment: null } : { answer: false })
              }
              onCommentChange={(comment) => answers.upsert(requirement, { comment })}
              onAttachmentsChange={(attachmentIds) => answers.upsert(requirement, { attachmentIds })}
            />
          ))}
        </Stack>
      </CardBody>
    </Card>
  );
};

const CoverLetterSection = ({ bidId, readOnly }: SectionProps) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus } = useEditor<BidDto, BidPatchDto>(bidId);
  const isSaving = fieldStatus("coverLetter") === "saving" || fieldStatus("coverLetter") === "pending";

  return (
    <Card variant="outline" rounded="xl">
      <CardBody>
        <Flex align="center" gap="2" mb="4">
          <Heading size="sm">{t("Cover letter")}</Heading>
          <Badge bg="smBackgroundSecondary" color="smMuted" px="2" borderRadius="md" textTransform="none">
            {t("Optional")}
          </Badge>
          {isSaving && <Spinner size="sm" color="smMuted" />}
        </Flex>
        <FormControl>
          <FormLabel fontSize="sm">{t("Message")}</FormLabel>
          <TipTap
            content={state.coverLetter ?? ""}
            onUpdate={(value) => { if (!readOnly) updateField({ coverLetter: value }, { debounceMs: 300 }); }}
            editable={!readOnly}
            toolbar={limitedToolbar}
            debounceWait={0}
          />
        </FormControl>
      </CardBody>
    </Card>
  );
};
